<script lang="ts">
	import type { getRestaurantState } from "$lib/serv/restaurant";

	import { cart$, cartItemQuantity$, checkout, deliverySubject$, editDelivery, editItemQuantity, suite$ as _suite$ } from "$lib/serv/food-cart";
	import { capitalizeFirstLetter, extractErrorMessage, formatCurrency, formatFoodReadyTime, merchantNameSuburbSlug } from "$lib/util/format";
	import { DeliveryOption, type Cart } from "$lib/def/food-cart";
	import { query, queryFrom } from "$lib/util/url";
	import { createEventDispatcher, onDestroy } from "svelte";
	import { profile$ } from "$lib/serv/core/auth";
	import { Vertical } from "$lib/def/verticals";
	import { fly } from "svelte/transition";
	import { goto } from "$app/navigation";
	import { firstValueFrom, shareReplay } from "rxjs";
	import { toastError } from "$lib/serv/toast";
	import { gatherRestaurantCache, KEY_FOOD_ITEM } from "$lib/serv/food-landing";
	import { setItem } from "$lib/serv/core/local-store";

	import SelfCollect from "$lib/ui/shared/icons/SelfCollect.svelte";
	import FoodCartItemGroup from "./FoodCartItemGroup.svelte";
	import CartComponent from "$lib/ui/shared/Cart.svelte";
	import Quantity from "$lib/ui/shared/Quantity.svelte";
	import SignIn from "$lib/ui/profile/SignIn.svelte";
	import Modal from "$lib/ui/shared/Modal.svelte";
	import ToolTip, { ToolPosition } from "$lib/ui/shared/ToolTip.svelte";
	import Totals from '$lib/ui/order/Totals.svelte';
  import {getMenuDealLabel} from "../menu/server";
    import {makeRestPDPSlug} from "../menu/server";
import { thumbor } from "$lib/serv/image-details";

	export let expanded = false;
	export let searchParams: Record<string, string>;
	export let vertical: Vertical = Vertical.Restaurant;
	export let currentRouteId: string;

	let showLogin = false;
	let showCollectNotice = false;
	let loading = false;

	const dispatch = createEventDispatcher();

	// function changeDelivery(e: Event | Event & { currentTarget: HTMLSelectElement }) {
	// 	editDelivery({ type: (e.currentTarget as HTMLElement & { value: DeliveryOption }).value as DeliveryOption });
	// }

	async function cont(cart: Cart, acceptCollection?: boolean) {
		loading = true;
		const profile = await firstValueFrom(profile$);

		if (!profile) return showLogin = true;

		if (cart.cart_type === DeliveryOption.Collect && !acceptCollection) return showCollectNotice = true;
		
		checkout(cart.uuid).subscribe({
			next: (res) => {
				goto(`/${vertical}/order/${res.id}/checkout`);
			},
			error: (er) => {
				toastError(extractErrorMessage(er, 'Failed to checkout'));
				loading = false;
			},
		});
	}

	let stateId: number | undefined;
	let promiseState: Promise<ReturnType<typeof getRestaurantState> | null> | undefined;
	async function getRestState(restaurantId?: number) {
		if (!restaurantId) return null;
		if (stateId === restaurantId) return promiseState;
		stateId = restaurantId;
		promiseState = gatherRestaurantCache(restaurantId).then((res) => {
			// putting this in seperate function triggered by template '@const' caused endless loop
			// if (!res?.hasDelivery) editDelivery({ type: DeliveryOption.Collect });
			return res;
		});
		return promiseState;
	}

	function makeEditHref(index:number, item: {item_id:number,section_id:number},cart:{menu_id:number,restaurant:{id:number,name:string,suburb:string}}) {
		const onMerchantPage = currentRouteId === '/(app)/delivery/[vertical=vertical]/[name]/[id]';
		const q = queryFrom(searchParams).set(KEY_FOOD_ITEM, makeRestPDPSlug(item.item_id,item.section_id,cart.menu_id,cart.restaurant.id)).set('edit',index)
		return q.toUrl(onMerchantPage ? '' : `/delivery/${vertical}/${merchantNameSuburbSlug(cart.restaurant.name,cart.restaurant.suburb)}/${cart.restaurant.id}`);
	}

	// workaround for Svelte 5 late template subscription
	const suite$ = _suite$.pipe(shareReplay(1));
	const subSuite = suite$.subscribe();
	onDestroy(() => {
		subSuite.unsubscribe();
	})
</script>


<CartComponent {expanded}>
	{@const cart = $cart$}
	{@const itemCount = $cartItemQuantity$}
	{@const promiseState = getRestState(cart?.restaurant?.id)}
	
	<svelte:fragment slot="header">
		{@const cart = $cart$}
		{@const itemCount = cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0}
		{@const restLogo = thumbor(cart?.restaurant?.logo, 200)}
		{#if itemCount}
			{#if restLogo}
				<div class="logo">
					<img src={restLogo} alt="Restaurant logo" />
				</div>
			{/if}
			<a href={queryFrom(searchParams).toUrl(`/delivery/${vertical}/${cart.restaurant.name}-${cart.restaurant.suburb ?? ''}/${cart?.restaurant?.id}`)} class="fill-space ellipses">
				<h5 class="headline-s bold m-0">Review cart</h5>
				<span class="label-xl medium m-0" data-testid="num-items">{itemCount ?? 0} item{itemCount === 1 ? '' : 's'}</span>
				<span class="label-xl medium book medium-importance m-0" data-testid="rest-name">&middot; {cart.restaurant.name}</span>
			</a>
			<div>
				<button on:click={() => dispatch('showCartToggle', false)} class="close" data-testid="hide-cart">
					<span class="flex icon-close-28"></span>
				</button>
			</div>	
		{:else}
			<div class="fill-space">
				<h5 class="headline-s bold m-0">Review cart</h5>
				<span class="label-xl medium m-0" data-testid="num-items">0 items</span>
			</div>
			<div>
				<button on:click={() => dispatch('showCartToggle', false)} class="close" data-testid="hide-cart">
					<span class="flex icon-close-28"></span>
				</button>
			</div>
		{/if}
	</svelte:fragment>


	<!--<svelte:fragment slot="subheader"></svelte:fragment>-->

	{#if itemCount}
		<div class="subheader">
			{#await promiseState then state}
				<p class="label-xl medium fill-space">
					{#if cart.type === DeliveryOption.Collect}
						{#if state?.prep_time}
							Collect in {formatFoodReadyTime(state.prep_time)}
						{:else if cart.collect_time_mins}
							Collect in {formatFoodReadyTime(cart.collect_time_mins)}
						{/if}
					{:else if cart.type === DeliveryOption.Delivery}
						{#if state?.prep_time && state?.estimated_delivery_time_for_distance}
							Delivery in {formatFoodReadyTime(state.prep_time + state.estimated_delivery_time_for_distance)}
						{:else if cart.deliver_time_mins}
							Delivery in {formatFoodReadyTime(cart.deliver_time_mins)}
						{/if}
					{/if}
				</p>
				<div class="pills m-0">
					{#if state?.hasDelivery}
						<label>
							<input type=radio name="delivery" bind:group={$deliverySubject$.type} value={DeliveryOption.Delivery}>
							<span>Deliver</span>
						</label>
					{/if}
					{#if state?.hasCollect}
						<label>
							<input type=radio name="delivery" bind:group={$deliverySubject$.type} value={DeliveryOption.Collect}>
							<span>Collect</span>
						</label>
					{/if}
				</div>
			{/await}
		</div>
	{/if}

	{#if itemCount}
		{#each cart.items as item, index (item.variant_id + index)}
			{#if item.quantity > 0}
				<div class="item" data-testid="cart-item" out:fly={{ x: 50, duration: 100 }}>

					<div class="flex mobile small-gap">

						<h4 class="basis-50 label-xl bold m-0" data-testid="item-name">
							{item.item_name}
							{#if item.collect_only}
								<br />
								<span class="pill small label-s bold m-0">Collect only</span>
							{/if}
              {#if item.menu_deal_action === "buy_1_get_1_free"}
                <br>
                <span class="label-s bold promo-pill m-0">{getMenuDealLabel(item.menu_deal_action)}</span>
              {/if}
						</h4>
            <div class="flex-column">
						  <div class="text-right label-xl bold m-0" data-testid="item-price">{formatCurrency(item.price)}</div>
              {#if item.old_price}
						    <div class="text-right label-xl m-0 grey strike-through" data-testid="item-old-price">{formatCurrency(item.old_price * item.quantity)}</div>
              {/if}
            </div>

						<h4 class="basis-50 label-l book medium-importance m-0" data-testid="variant-name">{item.variant_name || '----'}</h4>
						<div class="text-right label-l book medium-importance m-0" data-testid="variant-price">{formatCurrency(item.item_price)}</div>

						<FoodCartItemGroup groups={item.extras} groupType="extra" />
						<FoodCartItemGroup groups={item.options} groupType="option" />

						{#if item.addons?.length}
							{#each item.addons as addon}
								{#each addon.items as item}
									<div class="basis-50 label-l book medium-importance m-0" data-testid="addon-name">{item.name}</div>
									{#if item.price}
										<div class="text-right label-l book medium-importance m-0" data-testid="addon-price">{formatCurrency(item.price)}</div>
									{:else}
										<span data-testid="addon-price">--</span>
									{/if}

									<FoodCartItemGroup groups={item.extras} groupType="addon-extra" />
									<FoodCartItemGroup groups={item.options} groupType="addon-option" />

								{/each}
							{/each}
						{/if}

					</div>
					<div class="flex-row justify-between gap">
						<a href={makeEditHref(index,item,cart)} class="button secondary square inline-flex-row align-center" data-testid="edit-item">
							<span class="icon-edit-28"></span>
							Edit item
						</a>			
						<div class="text-right">
							<Quantity quantity={item.quantity} max={item.maximum_select} step={item.menu_deal_action === "buy_1_get_1_free" ? 2 : 1} on:update={e => editItemQuantity(index, e.detail)} />
						</div>			
					</div>
					<!--
					<div class="text-right">
						<Quantity quantity={item.quantity} on:update={e => editItemQuantity(index, e.detail)} />
					</div>-->
				</div>
			{/if}
		{/each}

		<div class="notes">
			<p class="label-xl bold">Add exclusions & allergy notes</p>
			<label>
				<!-- bind:value={order_notes} -->
				<textarea
					placeholder="e.g. No mushrooms please"
					rows="4" cols="50"
					value={cart.order_notes ?? ''}
					on:input={(e) => editDelivery({ order_notes: e.currentTarget.value })} />
			</label>
		</div>

		<div class="footer">
			<Totals order={cart} />
		</div>		

	{:else}
		<div class="flex-column height-100 margin"> <!-- justify-center -->
			{#await import('$lib/ui/shared/icons/CartEmptyFood.svelte')}
				Please wait...
			{:then CartEmptyFood}
				<CartEmptyFood.default />
			{:catch error}
				Failed to load CartEmptyFood
			{/await}
			<div class="text-center gap medium-importance">Your restaurant cart is empty.</div>
			<button class="button" on:click={() => dispatch('showCartToggle', false)} data-testid="empty-cart-cta-btn">Order now</button>
		</div>
	{/if}

	<svelte:fragment slot="footer">
		{@const cart = $cart$}
		{@const itemCount = cart?.items?.map((item) => item.quantity).reduce((a, b) => a + b, 0) || 0}
		{#if cart && itemCount}
			<section class="footer">
				<div class="width-100 inline-flex-row mobile small-gap self-end">
					<div class="flex-column">
						<div class="label-s book medium-importance m-0">Total</div>
						<div class="headline-s bold m-0" data-testid="cart-total">
							{#if cart && cart?.items}
								{ cart.items.length > 0 ? formatCurrency(cart?.totals?.total || 0) : "R0"}
								{:else}
								R0
							{/if}
						</div>
					</div>
					<button on:click={() => cont(cart)} class="relative button fill-space" class:loading disabled={!$suite$?.isValid()} data-testid="checkout-btn">
						Continue to Checkout
						{#if $suite$?.hasErrors()}
							<ToolTip message={$suite$?.getError()?.message ?? 'Please amend your cart to checkout.'} pos={ToolPosition.TC} />
						{/if}
					</button>
				</div>
			</section>
		{/if}
	</svelte:fragment>

</CartComponent>

{#if showLogin}
	<Modal on:close={() => showLogin = false} id="signin">
		<SignIn on:ok={() => cont($cart$)}/>
	</Modal>
{/if}

{#if showCollectNotice}
	<Modal on:close={() => showCollectNotice = false} id="collect">
		<div class="max-w-400 m-auto text-center">
			<SelfCollect />
			<h3 class="padding-top-16 headline-s bold m-0">Self collection</h3>
			<p class="label-xl medium">Just checking that you know you will need to collect your order?</p>
			<button class="button" on:click={() => showCollectNotice = false}>Cancel</button>
			<button class="button" on:click={() => cont($cart$, true)}>OK</button>
		</div>
	</Modal>
{/if}

<style lang="scss">
	@use 'margin';
	.gap {
		@include margin.medium;
	}
	.margin {
		margin: 0 16px;
	}
</style>
